import {
  AlertTriangleIcon,
  ArrowLeftIcon,
  RefreshCwIcon,
  XCircleIcon,
} from 'lucide-react'
import React from 'react'

import { cn } from '@/lib/utils'
import { Button } from './button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from './card'

interface ErrorOverlayProps {
  isVisible: boolean
  title?: string
  description?: string
  onRetry?: () => void
  onDismiss?: () => void
  children?: React.ReactNode
  errorDetails?: string[]
}

export const ErrorOverlay: React.FC<ErrorOverlayProps> = ({
  isVisible,
  title = 'Algo salió mal',
  description = 'Encontramos un error al cargar este contenido. Por favor, inténtalo de nuevo.',
  onRetry,
  onDismiss,
  children,
  errorDetails,
}) => {
  if (!isVisible) {
    return <>{children}</>
  }

  return (
    <div className="relative min-h-screen">
      {/* Blurred background content */}
      <div className="pointer-events-none select-none">{children}</div>

      {/* Error overlay */}
      <div
        className={cn(
          'fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-black/20 via-black/40 ' +
            'to-black/20 backdrop-blur-sm duration-300 animate-in fade-in-0'
        )}
      >
        <Card
          className={cn(
            'mx-4 w-full max-w-lg border-destructive/20 bg-card/95 shadow-2xl backdrop-blur-sm ' +
              'duration-300 animate-in zoom-in-95 slide-in-from-bottom-4'
          )}
        >
          <CardHeader className="pb-4 text-center">
            <div
              className={cn(
                'mx-auto mb-4 flex h-20 w-20 animate-pulse items-center justify-center ' +
                  'rounded-full bg-destructive/10 ring-4 ring-destructive/5'
              )}
            >
              <XCircleIcon className="h-10 w-10 text-destructive drop-shadow-sm" />
            </div>
            <CardTitle className="text-xl font-semibold text-card-foreground">
              {title}
            </CardTitle>
            <CardDescription className="text-center leading-relaxed text-muted-foreground">
              {description}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4 pb-6 text-center">
            <div
              className={cn(
                'flex items-start justify-center gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 ' +
                  'text-sm text-amber-700 shadow-sm dark:border-amber-800/50 dark:bg-amber-950/30 dark:text-amber-300'
              )}
            >
              <AlertTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span className="text-left">
                Este error puede ser temporal. Intenta actualizar para recargar
                los datos.
              </span>
            </div>

            {errorDetails && errorDetails.length > 0 && (
              <div className="text-left">
                <details className="group overflow-hidden rounded-lg border border-border">
                  <summary
                    className={cn(
                      'cursor-pointer list-none bg-muted/30 px-4 py-3 text-sm font-medium ' +
                        'text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground'
                    )}
                  >
                    <span className="flex items-center gap-2">
                      Detalles técnicos
                      <span className="text-xs opacity-60 transition-transform group-open:rotate-180">
                        ▼
                      </span>
                    </span>
                  </summary>
                  <div className="border-t border-border bg-muted/20 p-4">
                    <div className="space-y-2 font-mono text-xs text-muted-foreground">
                      {errorDetails.map((detail, index) => (
                        <div
                          className="flex items-start gap-2 rounded border border-border/50 bg-background/50 p-2"
                          key={index}
                        >
                          <span className="font-bold text-destructive">•</span>
                          <span className="break-all">{detail}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </details>
              </div>
            )}
          </CardContent>

          <CardFooter className="flex gap-3 pt-0">
            {onDismiss && (
              <Button
                className="flex-1 gap-2 transition-transform hover:scale-105"
                onClick={onDismiss}
                variant="outline"
              >
                <ArrowLeftIcon className="h-4 w-4" />
                Regresar
              </Button>
            )}
            {onRetry && (
              <Button
                className="flex-1 gap-2 transition-transform hover:scale-105"
                onClick={onRetry}
              >
                <RefreshCwIcon className="h-4 w-4" />
                Intentar de nuevo
              </Button>
            )}
          </CardFooter>
        </Card>
      </div>
    </div>
  )
}
